/**
 * BusinessLicensePackagesScreen
 *
 * Available only for businesses with industry === 'software'.
 * Allows software companies to:
 *   1. Create license packages (product-type: software_license)
 *   2. View incoming orders from external sites (e.g. Repair Angel)
 *   3. Manage active subscriber licenses (activate / revoke / renew)
 *   4. See revenue overview per package
 *
 * Orders flow: external site → SL API → this screen via /api/businesses/:id/license-orders
 */

import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Constants ──────────────────────────────────────────────────────────────

const BILLING_PERIODS = [
  { id: 'monthly',  nl: 'Maandelijks',  en: 'Monthly' },
  { id: 'annual',   nl: 'Jaarlijks',    en: 'Annual'  },
  { id: 'one_time', nl: 'Eenmalig',     en: 'One-time'},
];

const LICENSE_STATUSES = [
  { id: 'active',    nl: 'Actief',      en: 'Active',    color: '#4AC87A' },
  { id: 'trial',     nl: 'Proefperiode',en: 'Trial',     color: '#C8A84B' },
  { id: 'expired',   nl: 'Verlopen',    en: 'Expired',   color: '#C05050' },
  { id: 'cancelled', nl: 'Opgezegd',    en: 'Cancelled', color: '#667799' },
  { id: 'pending',   nl: 'In behandeling', en: 'Pending',color: '#5B9ED0' },
];

const EMPTY_PACKAGE = {
  name: '', description: '', price_monthly: '', price_annual: '',
  currency: 'EUR', trial_days: 14, max_users: '', features: '',
  status: 'active', is_public: true,
};

const TABS = [
  { id: 'packages',  nl: 'Pakketten',  en: 'Packages'  },
  { id: 'orders',    nl: 'Bestellingen', en: 'Orders'  },
  { id: 'licenses',  nl: 'Licenties',  en: 'Licences'  },
  { id: 'revenue',   nl: 'Omzet',      en: 'Revenue'   },
];

// ── Styles ─────────────────────────────────────────────────────────────────

const S = {
  wrap: { padding: '0 0 80px' },
  header: { marginBottom: 24 },
  title: { fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 4 },
  sub: { fontSize: 13, color: C.silver },
  tabs: { display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid rgba(255,255,255,.08)` },
  tab: (active) => ({
    padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: active ? '#fff' : C.silver, borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
    background: 'transparent', border: 'none', fontFamily: 'inherit', transition: 'all .2s',
  }),
  card: { background: '#1e293b', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14, padding: 20, marginBottom: 14 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  btn: (variant = 'primary') => ({
    padding: '8px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
    cursor: 'pointer', border: 'none', fontFamily: 'inherit', transition: 'all .2s',
    background: variant === 'primary'   ? 'linear-gradient(135deg,#3b82f6,#7c3aed)'
              : variant === 'danger'    ? 'rgba(192,80,80,.2)'
              : variant === 'success'   ? 'rgba(74,200,122,.2)'
              : 'rgba(255,255,255,.06)',
    color: variant === 'danger' ? '#fca5a5' : variant === 'success' ? '#6ee7b7' : '#fff',
    border: variant === 'danger'  ? '1px solid rgba(192,80,80,.4)'
          : variant === 'success' ? '1px solid rgba(74,200,122,.4)'
          : variant === 'ghost'   ? '1px solid rgba(255,255,255,.12)'
          : 'none',
    boxShadow: variant === 'primary' ? '0 4px 14px rgba(59,130,246,.3)' : 'none',
  }),
  input: {
    width: '100%', background: '#0f172a', border: '1px solid rgba(255,255,255,.12)',
    borderRadius: 9, color: '#fff', fontSize: 13, padding: '9px 12px',
    fontFamily: 'inherit', outline: 'none',
  },
  label: { fontSize: 12, fontWeight: 600, color: C.silver, display: 'block', marginBottom: 6, letterSpacing: '.04em' },
  fg: { marginBottom: 14 },
  badge: (color) => ({
    display: 'inline-block', background: color + '22', color, border: `1px solid ${color}44`,
    borderRadius: 100, padding: '2px 8px', fontSize: 11, fontWeight: 700,
  }),
  stat: { textAlign: 'center', padding: '16px 20px', background: '#0f172a', borderRadius: 12, flex: 1 },
  statVal: { fontSize: 26, fontWeight: 900, color: '#fff', fontFamily: 'monospace', marginBottom: 2 },
  statLbl: { fontSize: 11, color: C.silver, letterSpacing: '.04em', textTransform: 'uppercase' },
};

// ── Sub-components ─────────────────────────────────────────────────────────

function StatBar({ stats, nl }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
      {stats.map(s => (
        <div key={s.label} style={S.stat}>
          <div style={S.statVal}>{s.value}</div>
          <div style={S.statLbl}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function PackageCard({ pkg, nl, onEdit, onDelete }) {
  return (
    <div style={S.card}>
      <div style={S.row}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 4 }}>{pkg.name}</div>
          <div style={{ fontSize: 12, color: C.silver, marginBottom: 8 }}>{pkg.description}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pkg.price_monthly && (
              <span style={S.badge('#3b82f6')}>€{pkg.price_monthly}/{nl ? 'mnd' : 'mo'}</span>
            )}
            {pkg.price_annual && (
              <span style={S.badge('#7c3aed')}>€{pkg.price_annual}/{nl ? 'jaar' : 'yr'}</span>
            )}
            {pkg.trial_days > 0 && (
              <span style={S.badge('#10b981')}>{pkg.trial_days}d {nl ? 'proef' : 'trial'}</span>
            )}
            {pkg.max_users && (
              <span style={S.badge('#f59e0b')}>{pkg.max_users} {nl ? 'gebruikers' : 'users'}</span>
            )}
            <span style={S.badge(pkg.status === 'active' ? '#4AC87A' : '#667799')}>
              {nl ? (pkg.status === 'active' ? 'Actief' : 'Inactief') : (pkg.status === 'active' ? 'Active' : 'Inactive')}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn('ghost')} onClick={() => onEdit(pkg)}>
            {nl ? 'Bewerken' : 'Edit'}
          </button>
          <button style={S.btn('danger')} onClick={() => onDelete(pkg.id)}>
            {nl ? 'Verwijder' : 'Delete'}
          </button>
        </div>
      </div>
      {pkg.features && (
        <div style={{ marginTop: 12, fontSize: 12, color: C.silver }}>
          {pkg.features.split(',').map(f => f.trim()).filter(Boolean).map(f => (
            <span key={f} style={{ marginRight: 10 }}>✓ {f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, nl, onActivate, onDelete }) {
  const status = LICENSE_STATUSES.find(s => s.id === order.status) || LICENSE_STATUSES[0];
  return (
    <div style={S.card}>
      <div style={S.row}>
        <div>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 3 }}>
            #{order.id} — {order.company_name || order.company}
          </div>
          <div style={{ fontSize: 12, color: C.silver, marginBottom: 8 }}>
            {order.contact_name} · {order.contact_email}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={S.badge('#3b82f6')}>{order.package_name || [order.license_type, order.license_tier].filter(Boolean).join(' — ')}</span>
            <span style={S.badge('#7c3aed')}>{order.billing_period}</span>
            <span style={S.badge(status.color)}>{nl ? status.nl : status.en}</span>
            {order.source && <span style={S.badge('#94a3b8')}>{order.source}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>
            €{Number(order.amount_total).toFixed(2)}
          </div>
          <div style={{ fontSize: 11, color: C.silver }}>{order.currency}</div>
          <div style={{ fontSize: 11, color: C.silver, marginTop: 4 }}>
            {new Date(order.created_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB')}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
            {order.status === 'pending' && (
              <button style={{ ...S.btn('success'), fontSize: 11 }} onClick={() => onActivate(order.id)}>
                {nl ? 'Activeer' : 'Activate'}
              </button>
            )}
            <button style={{ ...S.btn('danger'), fontSize: 11 }} onClick={() => onDelete(order.id)}>
              {nl ? 'Verwijder' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LicenseCard({ license, nl, onRevoke, onRenew }) {
  const status = LICENSE_STATUSES.find(s => s.id === license.status) || LICENSE_STATUSES[0];
  const expiry = license.expires_at ? new Date(license.expires_at) : null;
  const daysLeft = expiry ? Math.ceil((expiry - Date.now()) / 86400000) : null;
  return (
    <div style={S.card}>
      <div style={S.row}>
        <div>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 3 }}>{license.company_name}</div>
          <div style={{ fontSize: 12, color: C.silver, marginBottom: 8 }}>
            {license.contact_email} · {license.package_name}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={S.badge(status.color)}>{nl ? status.nl : status.en}</span>
            {daysLeft !== null && (
              <span style={S.badge(daysLeft < 7 ? '#C05050' : '#C8A84B')}>
                {daysLeft > 0 ? `${daysLeft}d ${nl ? 'resterend' : 'remaining'}` : nl ? 'Verlopen' : 'Expired'}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn('ghost')} onClick={() => onRenew(license.id)}>
            {nl ? 'Verlengen' : 'Renew'}
          </button>
          <button style={S.btn('danger')} onClick={() => onRevoke(license.id)}>
            {nl ? 'Intrekken' : 'Revoke'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Package form modal ─────────────────────────────────────────────────────

function PackageFormModal({ pkg, onSave, onClose, nl }) {
  const [form, setForm] = useState(pkg ? { ...pkg } : { ...EMPTY_PACKAGE });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,.12)', borderRadius: 18, padding: 28, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 20 }}>
          {pkg ? (nl ? 'Pakket bewerken' : 'Edit package') : (nl ? 'Nieuw pakket' : 'New package')}
        </h3>
        <div style={S.fg}>
          <label style={S.label}>{nl ? 'Naam' : 'Name'} *</label>
          <input style={S.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Starter" />
        </div>
        <div style={S.fg}>
          <label style={S.label}>{nl ? 'Beschrijving' : 'Description'}</label>
          <input style={S.input} value={form.description} onChange={e => set('description', e.target.value)} placeholder={nl ? 'Korte omschrijving...' : 'Short description...'} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={S.fg}>
            <label style={S.label}>{nl ? 'Prijs / maand (€)' : 'Price / month (€)'}</label>
            <input style={S.input} type="number" value={form.price_monthly} onChange={e => set('price_monthly', e.target.value)} placeholder="49" />
          </div>
          <div style={S.fg}>
            <label style={S.label}>{nl ? 'Prijs / jaar (€)' : 'Price / year (€)'}</label>
            <input style={S.input} type="number" value={form.price_annual} onChange={e => set('price_annual', e.target.value)} placeholder="39" />
          </div>
          <div style={S.fg}>
            <label style={S.label}>{nl ? 'Proefperiode (dagen)' : 'Trial period (days)'}</label>
            <input style={S.input} type="number" value={form.trial_days} onChange={e => set('trial_days', e.target.value)} placeholder="14" />
          </div>
          <div style={S.fg}>
            <label style={S.label}>{nl ? 'Max. gebruikers' : 'Max users'}</label>
            <input style={S.input} type="number" value={form.max_users} onChange={e => set('max_users', e.target.value)} placeholder={nl ? 'Leeg = onbeperkt' : 'Empty = unlimited'} />
          </div>
        </div>
        <div style={S.fg}>
          <label style={S.label}>{nl ? 'Functies (komma-gescheiden)' : 'Features (comma-separated)'}</label>
          <input style={S.input} value={form.features} onChange={e => set('features', e.target.value)} placeholder="Work orders, Invoicing, CRM" />
        </div>
        <div style={S.fg}>
          <label style={S.label}>{nl ? 'Status' : 'Status'}</label>
          <select style={S.input} value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">{nl ? 'Actief' : 'Active'}</option>
            <option value="draft">{nl ? 'Concept' : 'Draft'}</option>
            <option value="archived">{nl ? 'Gearchiveerd' : 'Archived'}</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button style={S.btn('ghost')} onClick={onClose}>{nl ? 'Annuleren' : 'Cancel'}</button>
          <button style={S.btn('primary')} onClick={() => onSave(form)}>
            {nl ? 'Opslaan' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function BusinessLicensePackagesScreen({ businessId, lang, role }) {
  const nl = lang === 'nl';
  const [activeTab, setActiveTab] = useState('packages');
  const [packages, setPackages]   = useState([]);
  const [orders, setOrders]       = useState([]);
  const [licenses, setLicenses]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editPkg, setEditPkg]     = useState(null);

  const canManage = ['owner', 'admin'].includes(role);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pkgRes, ordRes, licRes] = await Promise.all([
        fetch(`${BASE}/businesses/${businessId}/license-packages`),
        fetch(`${BASE}/businesses/${businessId}/license-orders`),
        fetch(`${BASE}/businesses/${businessId}/licenses`),
      ]);
      const [pkgData, ordData, licData] = await Promise.all([
        pkgRes.json(), ordRes.json(), licRes.json()
      ]);
      setPackages(Array.isArray(pkgData) ? pkgData : (pkgData.packages || []));
      setOrders(Array.isArray(ordData) ? ordData : (ordData.orders || []));
      setLicenses(Array.isArray(licData) ? licData : (licData.licenses || []));
    } catch (e) {
      setError(nl ? 'Kon data niet laden.' : 'Could not load data.');
    } finally {
      setLoading(false);
    }
  }, [businessId, nl]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const savePackage = async (form) => {
    const method = form.id ? 'PUT' : 'POST';
    const url    = form.id
      ? `${BASE}/businesses/${businessId}/license-packages/${form.id}`
      : `${BASE}/businesses/${businessId}/license-packages`;
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowForm(false); setEditPkg(null); fetchAll(); }
    } catch { /* ignore */ }
  };

  const deletePackage = async (id) => {
    if (!window.confirm(nl ? 'Weet je het zeker?' : 'Are you sure?')) return;
    await fetch(`${BASE}/businesses/${businessId}/license-packages/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  const activateOrder = async (orderId) => {
    await fetch(`${BASE}/businesses/${businessId}/license-orders/${orderId}/activate`, { method: 'POST' });
    fetchAll();
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm(nl ? 'Bestelling verwijderen?' : 'Delete this order?')) return;
    await fetch(`${BASE}/businesses/${businessId}/license-orders/${orderId}`, { method: 'DELETE' });
    fetchAll();
  };

  const revokeLicense = async (licId) => {
    if (!window.confirm(nl ? 'Licentie intrekken?' : 'Revoke this license?')) return;
    await fetch(`${BASE}/businesses/${businessId}/licenses/${licId}/revoke`, { method: 'POST' });
    fetchAll();
  };

  const renewLicense = async (licId) => {
    await fetch(`${BASE}/businesses/${businessId}/licenses/${licId}/renew`, { method: 'POST' });
    fetchAll();
  };

  // ── Stats ────────────────────────────────────────────────────────────────

  const totalMRR = licenses
    .filter(l => l.status === 'active' && l.billing_period === 'monthly')
    .reduce((s, l) => s + Number(l.amount_monthly || 0), 0);

  const totalARR = totalMRR * 12 +
    licenses
      .filter(l => l.status === 'active' && l.billing_period === 'annual')
      .reduce((s, l) => s + Number(l.amount_annual || 0), 0);

  const stats = [
    { value: packages.length,                                          label: nl ? 'Pakketten'   : 'Packages'   },
    { value: orders.filter(o => o.status === 'pending').length,       label: nl ? 'Nieuw'       : 'New orders' },
    { value: licenses.filter(l => l.status === 'active').length,      label: nl ? 'Actief'      : 'Active'     },
    { value: `€${totalMRR.toFixed(0)}`,                               label: 'MRR'                              },
    { value: `€${totalARR.toFixed(0)}`,                               label: 'ARR'                              },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>📦 {nl ? 'Licentie Pakketten' : 'License Packages'}</div>
        <div style={S.sub}>
          {nl
            ? 'Beheer uw softwarelicenties, inkomende orders en actieve abonnementen.'
            : 'Manage your software licence packages, incoming orders and active subscriptions.'}
        </div>
      </div>

      <StatBar stats={stats} nl={nl} />

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(tab => (
          <button key={tab.id} style={S.tab(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
            {nl ? tab.nl : tab.en}
            {tab.id === 'orders' && orders.filter(o => o.status === 'pending').length > 0 && (
              <span style={{ background: '#3b82f6', color: '#fff', borderRadius: 100, padding: '1px 6px', fontSize: 10, fontWeight: 800, marginLeft: 6 }}>
                {orders.filter(o => o.status === 'pending').length}
              </span>
            )}
          </button>
        ))}
        {canManage && activeTab === 'packages' && (
          <button style={{ ...S.btn('primary'), marginLeft: 'auto', fontSize: 12, padding: '7px 14px' }} onClick={() => { setEditPkg(null); setShowForm(true); }}>
            + {nl ? 'Nieuw pakket' : 'New package'}
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: C.silver }}>
          {nl ? 'Laden...' : 'Loading...'}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(192,80,80,.1)', border: '1px solid rgba(192,80,80,.3)', borderRadius: 10, padding: '12px 16px', color: '#fca5a5', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* PACKAGES TAB */}
          {activeTab === 'packages' && (
            <div>
              {packages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: C.silver }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 6 }}>
                    {nl ? 'Nog geen pakketten' : 'No packages yet'}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 20 }}>
                    {nl ? 'Maak uw eerste licentiepakket aan om te beginnen.' : 'Create your first licence package to get started.'}
                  </div>
                  {canManage && (
                    <button style={S.btn('primary')} onClick={() => { setEditPkg(null); setShowForm(true); }}>
                      + {nl ? 'Pakket aanmaken' : 'Create package'}
                    </button>
                  )}
                </div>
              ) : (
                packages.map(pkg => (
                  <PackageCard key={pkg.id} pkg={pkg} nl={nl}
                    onEdit={p => { setEditPkg(p); setShowForm(true); }}
                    onDelete={deletePackage}
                  />
                ))
              )}
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div>
              {orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: C.silver }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📥</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 6 }}>
                    {nl ? 'Nog geen orders ontvangen' : 'No orders received yet'}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {nl
                      ? 'Orders van klanten die via uw website of de Repair Angel site bestellen verschijnen hier.'
                      : 'Orders from customers purchasing via your website or Repair Angel site will appear here.'}
                  </div>
                </div>
              ) : (
                orders.map(order => (
                  <OrderCard key={order.id} order={order} nl={nl} onActivate={activateOrder} onDelete={deleteOrder} />
                ))
              )}
            </div>
          )}

          {/* LICENSES TAB */}
          {activeTab === 'licenses' && (
            <div>
              {licenses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: C.silver }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🔑</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 6 }}>
                    {nl ? 'Nog geen actieve licenties' : 'No active licences yet'}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {nl ? 'Actieve abonnementen worden hier weergegeven.' : 'Active subscriptions will be displayed here.'}
                  </div>
                </div>
              ) : (
                licenses.map(lic => (
                  <LicenseCard key={lic.id} license={lic} nl={nl}
                    onRevoke={revokeLicense} onRenew={renewLicense}
                  />
                ))
              )}
            </div>
          )}

          {/* REVENUE TAB */}
          {activeTab === 'revenue' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
                {[
                  { label: 'MRR', value: `€${totalMRR.toFixed(2)}`, sub: nl ? 'Maandelijks terugkerend' : 'Monthly recurring' },
                  { label: 'ARR', value: `€${totalARR.toFixed(2)}`, sub: nl ? 'Jaarlijks terugkerend'  : 'Annual recurring' },
                  { label: nl ? 'Actieve licenties' : 'Active licences', value: licenses.filter(l => l.status === 'active').length, sub: '' },
                ].map(s => (
                  <div key={s.label} style={{ ...S.card, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#3b82f6', fontFamily: 'monospace', marginBottom: 4 }}>{s.value}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{s.label}</div>
                    {s.sub && <div style={{ fontSize: 11, color: C.silver }}>{s.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Per-package breakdown */}
              <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 14 }}>
                  {nl ? 'Omzet per pakket' : 'Revenue per package'}
                </div>
                {packages.map(pkg => {
                  const pkgLicenses = licenses.filter(l => l.package_id === pkg.id && l.status === 'active');
                  const pkgMRR = pkgLicenses.filter(l => l.billing_period === 'monthly').reduce((s, l) => s + Number(l.amount_monthly || 0), 0);
                  return (
                    <div key={pkg.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                      <span style={{ fontSize: 14, color: '#fff' }}>{pkg.name}</span>
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: C.silver }}>{pkgLicenses.length} {nl ? 'actief' : 'active'}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6' }}>€{pkgMRR.toFixed(0)}{nl ? '/mnd' : '/mo'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Package form modal */}
      {showForm && (
        <PackageFormModal
          pkg={editPkg}
          nl={nl}
          onSave={savePackage}
          onClose={() => { setShowForm(false); setEditPkg(null); }}
        />
      )}
    </div>
  );
}
